import React, { useState, useRef, useCallback } from 'react';
import { useAddin } from '../App';
import MatterCard from './MatterCard';
import * as webhook from '../services/webhook';

/** Safely extract practice area display name (Clio returns an object). */
function getPracticeArea(m) {
    const pa = m.practiceArea || m.practice_area;
    if (!pa) return null;
    return typeof pa === 'object' ? pa.name : pa;
}

/**
 * MatterLookup — replaces the static MatterCard in ActionsTab.
 * Renders one of 5 states based on the matter lookup result.
 * Includes free-text matter search when auto-lookup doesn't find the right match.
 */
export default function MatterLookup() {
    const {
        lookupState,
        lookupMatters,
        linkedMatter,
        setLinkedMatter,
        handleLinkMatter,
        handleUnlinkMatter,
        handleRelookup,
        handleRefreshCache,
        senderInfo,
        userEmail,
        surface,
    } = useAddin();

    // ── Search state ──
    const [searchMode, setSearchMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const debounceRef = useRef(null);

    const enterSearch = () => {
        setSearchMode(true);
        setSearchQuery('');
        setSearchResults([]);
        setSearchError(null);
    };

    const exitSearch = () => {
        setSearchMode(false);
        setSearchQuery('');
        setSearchResults([]);
        setSearchError(null);
        if (debounceRef.current) clearTimeout(debounceRef.current);
    };

    const handleSearchChange = useCallback((e) => {
        const q = e.target.value;
        setSearchQuery(q);
        setSearchError(null);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (q.trim().length < 2) {
            setSearchResults([]);
            setSearching(false);
            return;
        }

        setSearching(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const result = await webhook.searchMatters({ userEmail, query: q.trim(), surface });
                setSearchResults(result?.matters || []);
            } catch (err) {
                setSearchError(err.message || 'Search failed');
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 350);
    }, [userEmail]);

    const handleSelectSearchResult = (matter) => {
        handleLinkMatter(matter);
        exitSearch();
    };

    // ── Search UI component ──
    const SearchPanel = () => (
        <div className="matter-search-panel">
            <div className="matter-search-input">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="6.5" cy="6.5" r="5" />
                    <line x1="10" y1="10" x2="15" y2="15" />
                </svg>
                <input
                    type="text"
                    placeholder="Search by name, number, or client…"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    autoFocus
                />
                {searching && <span className="search-spinner" />}
            </div>

            {searchError && (
                <div className="search-error">{searchError}</div>
            )}

            {searchResults.length > 0 && (
                <div className="matter-picker" style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '6px' }}>
                    {searchResults.map((m, i) => (
                        <div
                            key={m.matter_id || m.id || i}
                            className="matter-picker-option"
                            onClick={() => handleSelectSearchResult(m)}
                        >
                            <div className="picker-radio">
                                <div className="picker-radio-dot" />
                            </div>
                            <div className="picker-info">
                                <div className="picker-name">{m.name || m.matter_description}</div>
                                <div className="picker-meta">
                                    {m.number || m.matter_number}
                                    {getPracticeArea(m) && ` · ${getPracticeArea(m)}`}
                                    {m.client_name && ` · ${m.client_name}`}
                                    {' · '}
                                    <span className={`badge ${(m.status || m.matter_status || 'open').toLowerCase()}`}>
                                        {m.status || m.matter_status || 'Open'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!searching && searchQuery.trim().length >= 2 && searchResults.length === 0 && !searchError && (
                <div className="search-empty">No matters found for "{searchQuery}"</div>
            )}

            <button className="relink-btn" onClick={exitSearch} style={{ marginTop: '6px', width: '100%' }}>
                Cancel
            </button>
        </div>
    );

    // ── LOADING ──
    if (lookupState === 'loading') {
        return (
            <div className="matter-lookup">
                <div className="matter-skeleton">
                    <div className="skeleton-line w60" />
                    <div className="skeleton-line w40" />
                    <div className="skeleton-line w80" />
                </div>
            </div>
        );
    }

    // ── ERROR ──
    if (lookupState === 'error') {
        return (
            <div className="matter-lookup">
                {searchMode ? (
                    <SearchPanel />
                ) : (
                    <div className="no-match-card">
                        <div className="no-match-icon">⚠</div>
                        <div className="no-match-title">Matter lookup failed</div>
                        <div className="no-match-email">Could not reach N8N. Actions still available with manual selection.</div>
                        <div className="no-match-actions">
                            <button className="confirm-btn" onClick={handleRelookup}>
                                Retry
                            </button>
                            <button className="relink-btn" onClick={enterSearch}>
                                Search Matters
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ── LINKED (confirmed) ──
    if (lookupState === 'linked' && linkedMatter) {
        return (
            <div className="matter-lookup">
                {searchMode ? (
                    <SearchPanel />
                ) : (
                    <>
                        <MatterCard
                            name={linkedMatter.name || linkedMatter.matter_description}
                            number={linkedMatter.number || linkedMatter.matter_number}
                            status={linkedMatter.status || 'Open'}
                            practiceArea={linkedMatter.practiceArea || linkedMatter.practice_area}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                            <button className="relink-btn" onClick={handleUnlinkMatter}>
                                Unlink
                            </button>
                            <button className="relink-btn" onClick={enterSearch}>
                                Change
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // ── SUGGESTED (1 match — auto-suggest) ──
    if (lookupState === 'suggested' && lookupMatters?.length === 1) {
        const m = lookupMatters[0];
        return (
            <div className="matter-lookup">
                {searchMode ? (
                    <SearchPanel />
                ) : (
                    <>
                        <div className="section-label">Suggested Matter</div>
                        <MatterCard
                            label="Suggested"
                            name={m.name || m.matter_description}
                            number={m.number || m.matter_number}
                            status={m.status || m.matter_status || 'Open'}
                            practiceArea={m.practiceArea || m.practice_area}
                        />
                        <div className="suggest-actions">
                            <button
                                className="confirm-btn"
                                onClick={() => handleLinkMatter(m)}
                                id="confirm-matter-btn"
                            >
                                ✓ Confirm
                            </button>
                            <button
                                className="relink-btn"
                                onClick={enterSearch}
                                id="change-matter-btn"
                            >
                                Search
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // ── PICKER (2+ matches) ──
    if (lookupState === 'picker' && lookupMatters?.length > 0) {
        return (
            <div className="matter-lookup">
                {searchMode ? (
                    <SearchPanel />
                ) : (
                    <>
                        <div className="section-label">Select Matter ({lookupMatters.length} matches)</div>
                        <div className="matter-picker" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {lookupMatters.map((m, i) => (
                                <div
                                    key={m.matter_id || m.id || i}
                                    className="matter-picker-option"
                                    onClick={() => handleLinkMatter(m)}
                                >
                                    <div className="picker-radio">
                                        <div className="picker-radio-dot" />
                                    </div>
                                    <div className="picker-info">
                                        <div className="picker-name">{m.name || m.matter_description}</div>
                                        <div className="picker-meta">
                                            {m.number || m.matter_number}
                                            {getPracticeArea(m) && ` · ${getPracticeArea(m)}`}
                                            {' · '}
                                            <span className={`badge ${(m.status || m.matter_status || 'open').toLowerCase()}`}>
                                                {m.status || m.matter_status || 'Open'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button className="relink-btn" onClick={enterSearch} style={{ marginTop: '6px', width: '100%' }}>
                            Search for a different matter
                        </button>
                    </>
                )}
            </div>
        );
    }

    // ── NO MATCH ──
    if (lookupState === 'no_match') {
        return (
            <div className="matter-lookup">
                {searchMode ? (
                    <SearchPanel />
                ) : (
                    <div className="no-match-card">
                        <div className="no-match-icon">🔍</div>
                        <div className="no-match-title">No matter found</div>
                        <div className="no-match-email">
                            {senderInfo?.email || 'Unknown sender'} — no linked matters in Clio
                        </div>
                        <div className="no-match-actions">
                            <button className="confirm-btn" onClick={enterSearch} style={{ flex: 1 }}>
                                Search Matters
                            </button>
                            <button className="relink-btn" onClick={handleRefreshCache} title="Refresh Clio matter cache">
                                ↻ Refresh Cache
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Fallback — shouldn't normally render
    return (
        <div className="matter-lookup">
            <div className="matter-skeleton">
                <div className="skeleton-line w60" />
                <div className="skeleton-line w40" />
            </div>
        </div>
    );
}
